import {matchExpression} from '@packages/systems/wfdl';
import {constant} from '@packages/utilities/fp/utils';
import {pair, type Pair} from '@packages/utilities/Pair';
import {incrementGuid} from '@packages/utilities/guid';
import {
  EElement,
  EExtend,
  ERecord,
  EList,
  EUnion,
  ECase,
  ECall,
  EFunction,
  ESelect,
  EDistinct,
  ETyped,
  type Expression,
} from '@packages/systems/wfdl-ast';

type NextId = string;
type IdMap = {
  [oldId: string]: string;
};
export type UniqueIdsState = {
  id: NextId;
  idMap: IdMap;
};

export const instantiateExpressionWithUniqueIds =
  (expr: Expression) =>
  (id: string): Pair<UniqueIdsState, Expression> => {
    const state = {id, idMap: {}};
    const newExpr = _instantiate_mut(expr)(state);
    return pair(state, newExpr);
  };

export const instantiateExpressionWithUniqueIdsState =
  (expr: Expression) =>
  (seedState: UniqueIdsState): Pair<UniqueIdsState, Expression> => {
    const state = {
      id: seedState.id,
      idMap: {},
      seedIdMap: seedState.idMap,
    };
    const newExpr = _instantiate_mut(expr)(state);
    return pair({id: state.id, idMap: state.idMap}, newExpr);
  };

type InternalUniqueIdsState = {
  id: NextId;
  idMap: IdMap;
  seedIdMap?: IdMap;
};

const _instantiate_mut: (
  expr: Expression
) => (state: InternalUniqueIdsState) => Expression = matchExpression({
  Element: (el) => (state) => {
    let id = state.seedIdMap?.[el.id];
    if (!id) {
      id = state.id;
      state.id = incrementGuid(state.id);
    }
    state.idMap[el.id] = id;

    const data = _instantiate_mut(el.data)(state);

    /*
      we can't safely do an equality check on newData and the original here like we are
      doing in all of the other expression matches.

      The purpose of the function is to change the `id` of each element,
      so we can never just return the original element.
    */

    return EElement({
      id,
      type: el.type,
      data,
      ...(el.meta && {meta: el.meta}),
    });
  },
  Record: (props, original) => (state) => {
    let propertiesHaveChanged = false;

    const newProperties = Object.entries(props).reduce<
      Record<string, Expression>
    >((acc, [key, val]) => {
      acc[key] = _instantiate_mut(val)(state);

      if (val !== acc[key]) {
        propertiesHaveChanged = true;
      }

      return acc;
    }, {});

    if (!propertiesHaveChanged) {
      return original;
    }

    return ERecord(newProperties);
  },
  Extend:
    ({record, prop, value}, original) =>
    (state) => {
      const newRecord = _instantiate_mut(record)(state);
      const newValue = _instantiate_mut(value)(state);

      if (record === newRecord && value === newValue) {
        return original;
      }

      return EExtend({
        record: newRecord,
        prop,
        value: newValue,
      });
    },
  List: (list, original) => (state) => {
    let changed = false;
    const newItems = list.map((item) => {
      const newItem = _instantiate_mut(item)(state);
      if (item !== newItem) {
        changed = true;
      }
      return newItem;
    });

    if (!changed) {
      return original;
    }

    return EList(newItems);
  },
  Union:
    ({tag, value}, original) =>
    (state) => {
      const newValue = _instantiate_mut(value)(state);

      if (value === newValue) {
        return original;
      }

      return EUnion({tag, value: newValue});
    },
  Case:
    ({value, cases}, original) =>
    (state) => {
      const newValue = _instantiate_mut(value)(state);
      let casesHaveChanged = false;
      const newCases = Object.entries(cases).reduce<Record<string, Expression>>(
        (acc, [key, val]) => {
          acc[key] = _instantiate_mut(val)(state);

          if (val !== acc[key]) {
            casesHaveChanged = true;
          }

          return acc;
        },
        {}
      );

      if (value === newValue && !casesHaveChanged) {
        return original;
      }

      return ECase({
        value: newValue,
        cases: newCases,
      });
    },
  Call:
    ({fun, arg}, original) =>
    (state) => {
      const newFun = _instantiate_mut(fun)(state);
      const newArg = _instantiate_mut(arg)(state);

      if (fun === newFun && arg === newArg) {
        return original;
      }

      return ECall({
        fun: newFun,
        arg: newArg,
      });
    },
  Function:
    ({param, body}, original) =>
    (state) => {
      const newBody = _instantiate_mut(body)(state);

      if (body === newBody) {
        return original;
      }

      return EFunction({
        param,
        body: newBody,
      });
    },
  Select:
    ({from, prop}, original) =>
    (state) => {
      const newFrom = _instantiate_mut(from)(state);

      if (from === newFrom) {
        return original;
      }

      return ESelect({
        from: newFrom,
        prop,
      });
    },
  Distinct:
    ({name, value}, original) =>
    (state) => {
      const newValue = _instantiate_mut(value)(state);

      if (value === newValue) {
        return original;
      }

      return EDistinct({
        name,
        value: newValue,
      });
    },
  Typed:
    ({type, expr}, original) =>
    (state) => {
      const newExpr = _instantiate_mut(expr)(state);

      if (expr === newExpr) {
        return original;
      }

      return ETyped({type, expr: newExpr});
    },
  _: constant,
});
